'use client'

import {
  createProfileLink,
  updateProfileLink,
} from '@/app/profile-manager/settings/connected-accounts/actions/profile-links'
import { profileLinkSchema } from '@/app/profile-manager/settings/connected-accounts/profile-link-schema'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { profileLinkServices } from '@/resources/profile-link-services'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'

const emptyProfileLink = {
  service: '',
  username: '',
  showOnProfile: true,
}

export function ProfileLinkDialog({
  open,
  onOpenChange,
  profileLink,
  unavailableServices,
  onSaved,
}) {
  const form = useForm({
    resolver: zodResolver(profileLinkSchema),
    defaultValues: emptyProfileLink,
  })

  useEffect(() => {
    if (!open) return

    form.reset(
      profileLink
        ? {
            service: profileLink.service,
            username: profileLink.username,
            showOnProfile: profileLink.showOnProfile,
          }
        : emptyProfileLink,
    )
  }, [form, open, profileLink])

  const selectedService = useWatch({
    control: form.control,
    name: 'service',
  })
  const selectedServiceConfig = profileLinkServices.find(
    (service) => service.id === selectedService,
  )

  async function onSubmit(values) {
    const result = profileLink
      ? await updateProfileLink(profileLink.id, values)
      : await createProfileLink(values)

    if (!result.success) {
      Object.entries(result.errors || {}).forEach(([field, messages]) => {
        form.setError(field, { message: messages[0] })
      })
      toast.error(result.message)
      return
    }

    toast.success(result.message)
    onOpenChange(false)
    onSaved()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] border-neutral-blue-700 bg-neutral-blue-900 text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {profileLink ? 'Edit external link' : 'Add external link'}
          </DialogTitle>
          <DialogDescription>
            These links are supplied by you and are not verified by Grid Finder.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="space-y-5"
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
          >
            <FormField
              control={form.control}
              name="service"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={form.formState.isSubmitting}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {profileLinkServices.map((service) => (
                        <SelectItem
                          key={service.id}
                          value={service.id}
                          disabled={
                            unavailableServices.includes(service.id) &&
                            service.id !== profileLink?.service
                          }
                        >
                          {service.nicename}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {selectedServiceConfig?.descriptor || 'Username or link'}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      maxLength={500}
                      placeholder={
                        selectedServiceConfig
                          ? `${selectedServiceConfig.baseUrl}…`
                          : 'Select a service first'
                      }
                      disabled={!selectedService || form.formState.isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  {selectedServiceConfig && (
                    <FormDescription>
                      Paste a full link or enter the part after{' '}
                      {selectedServiceConfig.baseUrl}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showOnProfile"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 p-4">
                  <div className="space-y-1">
                    <FormLabel>Show on profile</FormLabel>
                    <FormDescription>
                      Display this link on your public profiles.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={form.formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="success"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
